import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import EeCategoryImport from "@/components/admin/products/EeCategoryImport";
import ImageMirrorCard from "@/components/admin/ImageMirrorCard";
import EeBrandImport from "@/components/admin/products/EeBrandImport";
import {
    RefreshCw,
    Play,
    Pause,
    X,
    Plus,
    Database,
} from "lucide-react";

export default function ScraperTab() {
    const [stats, setStats] = useState(null);
    const [pages, setPages] = useState(50);
    const [status, setStatus] = useState(null);
    const [busy, setBusy] = useState(null);  // "full" | "sync" | null

    const load = async () => {
        try {
            const [{ data: s }, { data: scr }] = await Promise.all([
                api.get("/admin/stats"),
                api.get("/admin/scrape/status"),
            ]);
            setStats(s);
            setStatus(scr);
            if (!scr.running && busy) setBusy(null);
        } catch (e) {
            console.error("scraper load", e);
        }
    };

    useEffect(() => { load(); }, []);
    useEffect(() => {
        if (!status?.running) return;
        const id = setInterval(load, 2000);
        return () => clearInterval(id);
    }, [status?.running]);

    const startFull = async () => {
        if (!confirm("Run FULL import? This modifies existing products. Pages per category: " + pages)) return;
        setBusy("full");
        try {
            await api.post("/admin/scrape", { max_pages_per_category: pages });
            load();
        } catch (e) {
            alert("Failed: " + (e?.response?.data?.detail || e.message));
            setBusy(null);
        }
    };


    const pauseRun = async () => { await api.post("/admin/scrape/pause"); load(); };
    const resumeRun = async () => { await api.post("/admin/scrape/resume"); load(); };
    const cancelRun = async () => {
        if (!confirm("Cancel the running scrape?")) return;
        await api.post("/admin/scrape/cancel"); load();
    };

    const running = !!status?.running;
    const paused = !!status?.paused;
    const mode = status?.mode;
    const progress = status?.products ?? 0;

    return (
        <div data-testid="admin-scraper-tab">
            <ImageMirrorCard />
            <EeBrandImport />
            <section className="border border-emerald-200 p-4 mb-6 max-w-3xl" data-testid="scraper-category-sync">
                <div className="font-display font-black uppercase mb-1">Sync missing sipas kategorisë</div>
                <EeCategoryImport onImported={load} />
            </section>
            <div className="border border-amber-200 bg-amber-50 p-3 mb-4 text-sm text-amber-900">
                <strong>Full import:</strong> Modifikon produktet ekzistuese. Përdor vetëm kur dëshiron të rifreskosh
                emrat / imazhet / kategoritë e gjithçkaje.
            </div>

            <div className="flex items-end gap-3 mb-4 flex-wrap">
                <div>
                    <label className="label-kicker mb-1 block">Pages per category</label>
                    <input
                        type="number" min={1} max={200} value={pages}
                        onChange={(e) => setPages(Math.max(1, Math.min(200, parseInt(e.target.value || "1", 10))))}
                        className="input-field !w-28"
                        data-testid="scrape-pages-input"
                        disabled={running}
                    />
                </div>
                <button
                    onClick={startFull}
                    disabled={running}
                    className="btn-outline"
                    data-testid="scrape-start-button"
                    title="Modifikon produktet ekzistuese"
                >
                    <Database className="w-4 h-4" /> Full re-import
                </button>
                {running && !paused && (
                    <button onClick={pauseRun} className="btn-outline" data-testid="scrape-pause-button">
                        <Pause className="w-4 h-4" /> Pause
                    </button>
                )}
                {running && paused && (
                    <button onClick={resumeRun} className="btn-primary" data-testid="scrape-resume-button">
                        <Play className="w-4 h-4" /> Resume
                    </button>
                )}
                {running && (
                    <button onClick={cancelRun} className="btn-outline" data-testid="scrape-cancel-button">
                        <X className="w-4 h-4" /> Cancel
                    </button>
                )}
                <button onClick={load} className="btn-outline" data-testid="scrape-refresh-button">
                    <RefreshCw className="w-3.5 h-3.5" /> Refresh
                </button>
            </div>

            {status && (
                <div className="font-mono text-xs space-y-1 bg-slate-50 border border-slate-200 p-3 max-w-2xl" data-testid="scrape-status-panel">
                    <div>
                        <span className="text-slate-500">STATUS:</span>{" "}
                        {running
                            ? (paused
                                ? <span className="text-amber-600">● PAUSED</span>
                                : <span className="text-emerald-600">● RUNNING</span>)
                            : <span className="text-slate-600">● IDLE</span>
                        }
                        {mode && <span className="ml-2 text-slate-400">({mode})</span>}
                    </div>
                    <div><span className="text-slate-500">PHASE:</span> {status.phase || "—"}</div>
                    <div><span className="text-slate-500">LAST RUN:</span> {status.last_run || "—"}</div>
                    <div><span className="text-slate-500">CATEGORIES:</span> {status.categories ?? 0}</div>
                    {mode === "missing_only" ? (
                        <>
                            <div><span className="text-slate-500">ADDED (new):</span> <span className="text-emerald-700 font-bold">{status.added ?? 0}</span></div>
                            <div><span className="text-slate-500">SKIPPED (already exist):</span> {status.skipped ?? 0}</div>
                            <div><span className="text-slate-500">TOTAL SEEN:</span> {progress}</div>
                        </>
                    ) : (
                        <div><span className="text-slate-500">PRODUCTS:</span> {progress}</div>
                    )}
                    {status.current_category && (
                        <div><span className="text-slate-500">CURRENT:</span> {status.current_category}{status.page ? ` · faqja ${status.page}` : ""}</div>
                    )}
                    {status.error && <div className="text-red-600">ERROR: {status.error}</div>}
                </div>
            )}

            {stats && (
                <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-2 max-w-2xl">
                    <div className="border border-slate-200 bg-white p-3">
                        <div className="label-kicker text-slate-500">Total products in DB</div>
                        <div className="font-display font-black text-2xl">{stats.products}</div>
                    </div>
                    <div className="border border-slate-200 bg-white p-3">
                        <div className="label-kicker text-slate-500">Total categories</div>
                        <div className="font-display font-black text-2xl">{stats.categories}</div>
                    </div>
                </div>
            )}
        </div>
    );
}
